import { DateTime } from 'luxon';
import { derived, get, readable, writable } from 'svelte/store';

// Business timezone (stored here to prevent circular dependencies with i18n)
export const businessTimezone = writable<string>('UTC'); // Pacific/Kiritimati (UTC+14), Pacific/Howland (UTC-12)

// Simulated offset in milliseconds (null when real-time is active)
export const timeOffsetMs = writable<number | null>(null);

// Is simulated time travel active?
export const isTimeSimulated = derived(
	timeOffsetMs,
	($offset) => $offset !== null && $offset !== 0
);

export function computeCurrentDateTime(): DateTime {
	const offset = get(timeOffsetMs);
	const zone = get(businessTimezone);
	const base = offset ? DateTime.now().plus({ milliseconds: offset }) : DateTime.now();
	return base.setZone(zone);
}

export function setTimeTravel(targetDate: DateTime | string): void {
	const dt = typeof targetDate === 'string' ? DateTime.fromISO(targetDate) : targetDate;
	if (!dt.isValid) return;
	const realNow = DateTime.now();
	const offset = dt.diff(realNow).as('milliseconds');
	timeOffsetMs.set(offset);
}

export function advanceTimeBy(amount: {
	days?: number;
	weeks?: number;
	months?: number;
	hours?: number;
}): void {
	const currentSimulated = computeCurrentDateTime();
	const target = currentSimulated.plus(amount);
	setTimeTravel(target);
}

export function resetTimeTravel(): void {
	timeOffsetMs.set(null);
}

export const now = readable(computeCurrentDateTime(), function start(set) {
	set(computeCurrentDateTime());
	const interval = setInterval(() => {
		set(computeCurrentDateTime());
	}, 1000);

	const unsubscribe = timeOffsetMs.subscribe(() => {
		set(computeCurrentDateTime());
	});

	return function stop() {
		clearInterval(interval);
		unsubscribe();
	};
});

export const currentBrowserTime = readable(DateTime.now(), function start(set) {
	const interval = setInterval(() => {
		set(DateTime.now());
	}, 1000);
	return function stop() {
		clearInterval(interval);
	};
});

let currentTodayDay = computeCurrentDateTime().startOf('day');

const todayStore = readable(currentTodayDay, function start(set) {
	function checkAndUpdateDay() {
		const currentNow = computeCurrentDateTime();
		if (!currentNow.hasSame(currentTodayDay, 'day')) {
			currentTodayDay = currentNow.startOf('day');
			set(currentTodayDay);
		}
	}

	currentTodayDay = computeCurrentDateTime().startOf('day');
	set(currentTodayDay);
	const interval = setInterval(checkAndUpdateDay, 5000);

	const unsubscribeOffset = timeOffsetMs.subscribe(() => {
		checkAndUpdateDay();
	});

	const unsubscribeTimezone = businessTimezone.subscribe(() => {
		currentTodayDay = computeCurrentDateTime().startOf('day');
		set(currentTodayDay);
	});

	return function stop() {
		clearInterval(interval);
		unsubscribeOffset();
		unsubscribeTimezone();
	};
});

export const today = derived([businessTimezone, todayStore], ([$businessTimezone, $todayStore]) =>
	$todayStore.setZone($businessTimezone)
);

export const currentTimeFormatted = derived([now, businessTimezone], ([$now, $zone]) =>
	$now.setZone($zone).toLocaleString({ timeStyle: 'short' })
);
export const currentDateFormatted = derived([now, businessTimezone], ([$now, $zone]) =>
	$now.setZone($zone).toLocaleString({ dateStyle: 'medium' })
);
